import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import CameraFab from './components/CameraFab';
import BuildStamp from './components/BuildStamp';
import { IconGithub } from './components/Icon';
import { useSession } from './lib/session';

import Login from './routes/Login';
import EmailCode from './routes/EmailCode';
import AuthCallback from './routes/AuthCallback';
import Welcome from './routes/Welcome';
import Now from './routes/Now';
import Schedule from './routes/Schedule';
import EventDetail from './routes/EventDetail';
import MapPage from './routes/MapPage';
import Upload from './routes/Upload';
import Gallery from './routes/Gallery';
import PhotoDetail from './routes/PhotoDetail';
import Credits from './routes/Credits';
import Profile from './routes/Profile';
import AdminGuests from './routes/AdminGuests';
import Scorecard from './routes/Scorecard';
import Display from './routes/Display';
import NotFound from './routes/NotFound';

function SiteFoot() {
  return (
    <footer className="site-foot">
      <span>Hand-built by Meg and Videet at unreasonable hours. Nobody else gets your data — not even an app.</span>
      <div style={{ flex: 1 }} />
      <a href="https://github.com/megandvideet/site" rel="noreferrer noopener" target="_blank">
        <IconGithub /> github.com/megandvideet/site
      </a>
      {/* Last, and quieter than everything else in here: it is diagnostic
          rather than something a guest is meant to read. The sign-in screens
          carry their own copy — they are routed outside this shell. */}
      <BuildStamp />
    </footer>
  );
}

/** Everything except the login screens is behind a session (D2). */
function Guarded({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (!session) {
    // Preserve intent: a guest bounced to login lands back where they were going.
    return <Navigate to="/" replace state={{ from: location.pathname + location.search }} />;
  }
  if (session.needsWelcome && location.pathname !== '/welcome') {
    return <Navigate to="/welcome" replace />;
  }
  return <>{children}</>;
}

function Shell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <div className="app">
      <Header />
      <main className="app-main container">{children}</main>
      {/* A shortcut to the upload screen, from the upload screen, was one more
          thing sitting in the corner the new action bar needs. */}
      {pathname !== '/upload' && <CameraFab />}
      <SiteFoot />
    </div>
  );
}

const guarded = (element: React.ReactNode) => <Guarded><Shell>{element}</Shell></Guarded>;

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login/email" element={<EmailCode />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/welcome" element={<Guarded><Welcome /></Guarded>} />
      <Route path="/display" element={<Display />} />

      <Route path="/now" element={guarded(<Now />)} />
      <Route path="/schedule" element={guarded(<Schedule />)} />
      <Route path="/schedule/:id" element={guarded(<EventDetail />)} />
      <Route path="/map" element={guarded(<MapPage />)} />
      <Route path="/map/:venueId" element={guarded(<MapPage />)} />
      <Route path="/upload" element={guarded(<Upload />)} />
      <Route path="/gallery" element={guarded(<Gallery />)} />
      <Route path="/gallery/:photoId" element={<Guarded><PhotoDetail /></Guarded>} />
      <Route path="/scorecard" element={guarded(<Scorecard />)} />
      <Route path="/credits" element={guarded(<Credits />)} />
      <Route path="/me" element={guarded(<Profile />)} />
      <Route path="/me/guests" element={guarded(<AdminGuests />)} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
